import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Sparkles,
  Check,
  Star,
  Crown,
  Upload,
  Wand2,
  MailCheck,
} from "lucide-react";
import usePageMeta from "@/lib/usePageMeta";
import { useCheckout } from "@/lib/checkout";

const H2 = ({ children }) => (
  <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white mt-16 mb-6">
    {children}
  </h2>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-3 text-[15px] text-zinc-200 leading-relaxed">
    <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-500/30 mt-0.5">
      <Check className="h-3 w-3 text-emerald-400" />
    </span>
    <span>{children}</span>
  </li>
);

const plans = [
  {
    name: "BASIC",
    price: "$12.13",
    Icon: Sparkles,
    planId: "basic",
    tag: "Get Basic",
    highlight: false,
    summary: "Professional AI resume, ATS-optimized, delivered in minutes.",
  },
  {
    name: "PRO",
    price: "$26.01",
    Icon: Star,
    planId: "pro",
    tag: "Get Pro",
    highlight: true,
    summary: "Everything in Basic + AI analysis report, ATS score, 3 revisions included.",
  },
  {
    name: "ELITE",
    price: "$51.21",
    Icon: Crown,
    planId: "elite",
    tag: "Get Elite",
    highlight: false,
    summary: "Full career rebrand: job targeting, interview tips, ultra-premium design.",
  },
];

const faqs = [
  {
    q: "How fast will I receive my resume?",
    a: "Instantly — our AI analyzes, rewrites, and delivers your new resume in minutes, right on your screen.",
  },
  {
    q: "Will my resume pass ATS?",
    a: "Yes. Every resume is engineered to pass the Applicant Tracking Systems used by 99% of Fortune 500 companies — proper headings, clean formatting, and keyword-rich content tailored to your target roles.",
  },
  {
    q: "Is there a subscription?",
    a: "No. Every plan is a one-time payment. You pay once, receive your resume, and keep it forever.",
  },
];

export default function ResumeWritingService() {
  const { startCheckout } = useCheckout();
  usePageMeta({
    title: "Resume Writing Service | Done-For-You AI Resumes in Minutes",
    description:
      "Professional AI resume writing service. Upload your CV and receive a rewritten, ATS-friendly resume in minutes. One-time payment, no subscription. From $12.13.",
    path: "/resume-writing-service",
  });

  return (
    <div
      className="relative min-h-screen bg-black text-white"
      data-testid="rws-page"
    >
      <div
        aria-hidden
        className="absolute -top-32 left-1/2 -translate-x-1/2 h-[520px] w-[520px] rounded-full bg-blue-600/15 blur-[140px]"
      />
      <div
        aria-hidden
        className="absolute top-40 -right-24 h-[420px] w-[420px] rounded-full bg-purple-600/15 blur-[140px]"
      />
      <div aria-hidden className="absolute inset-0 grid-bg opacity-60" />

      <header className="relative">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 pt-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="rws-logo-link">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-blue-500/80 to-purple-600/80 ring-1 ring-white/10">
              <Sparkles className="h-3.5 w-3.5 text-white" />
            </span>
            <span className="text-sm font-semibold tracking-tight text-white">
              CV AI <span className="text-zinc-400 font-medium">PRO</span>
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors"
            data-testid="rws-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to home
          </Link>
        </div>
      </header>

      <main className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">
            Done-for-you resume writing
          </p>
          <h1
            className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter text-white text-balance"
            data-testid="rws-h1"
          >
            Professional Resume Writing Service
          </h1>
          <p
            className="mt-6 text-zinc-400 leading-relaxed text-balance text-[17px]"
            data-testid="rws-intro"
          >
            Get a professionally rewritten resume without the $300–$1,200 price
            tag of traditional writers. CV AI PRO delivers done-for-you resume
            writing: you upload, we polish, you apply.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => startCheckout("cv_pro")}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-7 py-3 text-sm font-medium hover:bg-zinc-200 transition-colors"
              data-testid="rws-cta-top"
            >
              Get My Resume
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <Link
              to="/ai-resume-builder"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 py-3 text-sm font-medium text-white hover:bg-white/10 transition-colors"
              data-testid="rws-link-builder"
            >
              How our AI builder works
            </Link>
          </div>
        </motion.div>

        <H2>What you get</H2>
        <ul className="space-y-3">
          <Bullet>Full rewrite by our AI engine</Bullet>
          <Bullet>ATS-optimized layout</Bullet>
          <Bullet>Modern professional design</Bullet>
          <Bullet>Delivered in minutes by AI</Bullet>
          <Bullet>One-time payment, no subscription</Bullet>
        </ul>

        <H2>How it works</H2>
        <ol className="space-y-4">
          {[
            { Icon: Upload, text: "Upload your current CV." },
            { Icon: Wand2, text: "AI rewrites and redesigns it." },
            {
              Icon: MailCheck,
              text: "Download your ATS-ready PDF minutes later.",
            },
          ].map(({ Icon, text }, i) => (
            <li
              key={i}
              className="flex items-start gap-4 rounded-2xl border border-white/10 bg-zinc-950/60 p-5"
              data-testid={`rws-step-${i}`}
            >
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-gradient-to-br from-zinc-900 to-black">
                <Icon className="h-4 w-4 text-white/90" />
              </span>
              <div>
                <p className="text-xs text-zinc-500 font-mono">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <p className="mt-1 text-[15px] text-zinc-200">{text}</p>
              </div>
            </li>
          ))}
        </ol>

        <H2>Pricing</H2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {plans.map((p) => (
            <div
              key={p.name}
              className={`relative rounded-2xl border ${
                p.highlight ? "border-white/20" : "border-white/10"
              } bg-zinc-950/60 p-5 flex flex-col`}
              data-testid={`rws-plan-${p.name.toLowerCase()}`}
            >
              <div className="flex items-center gap-2">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-white/10 bg-gradient-to-br from-zinc-900 to-black">
                  <p.Icon className="h-3.5 w-3.5 text-white/90" />
                </span>
                <p className="text-xs font-medium tracking-wide text-white">
                  {p.name}
                </p>
                {p.highlight && (
                  <span className="ml-auto inline-flex items-center rounded-full bg-white text-black px-2 py-0.5 text-[9px] font-medium">
                    POPULAR
                  </span>
                )}
              </div>
              <p className="mt-4 text-2xl font-semibold tracking-tighter text-white">
                {p.price}
              </p>
              <p className="mt-2 text-xs text-zinc-400 leading-relaxed flex-1">
                {p.summary}
              </p>
              <button
                onClick={() => startCheckout(p.planId)}
                className={`mt-5 inline-flex w-full items-center justify-center rounded-full px-4 py-2 text-xs font-medium transition-colors ${
                  p.highlight
                    ? "bg-white text-black hover:bg-zinc-200"
                    : "bg-white/10 text-white border border-white/15 hover:bg-white/15"
                }`}
                data-testid={`rws-plan-cta-${p.planId}`}
              >
                {p.tag}
              </button>
            </div>
          ))}
        </div>

        <H2>FAQ</H2>
        <div className="space-y-4">
          {faqs.map((f, i) => (
            <div
              key={i}
              className="rounded-2xl border border-white/10 bg-zinc-950/60 p-5"
              data-testid={`rws-faq-${i}`}
            >
              <p className="text-[15px] text-white font-medium">{f.q}</p>
              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                {f.a}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-16 flex flex-col items-center gap-3 rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-950 to-black p-8 text-center">
          <p className="text-lg font-medium text-white">
            Ready for a recruiter-ready resume?
          </p>
          <button
            onClick={() => startCheckout("cv_pro")}
            className="mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-7 py-3 text-sm font-medium hover:bg-zinc-200 transition-colors"
            data-testid="rws-cta-bottom"
          >
            Get My Resume
            <ArrowRight className="h-4 w-4" />
          </button>
          <p className="mt-3 text-xs text-zinc-500">
            Or explore our{" "}
            <Link to="/" className="text-zinc-300 hover:text-white underline underline-offset-2">
              full homepage
            </Link>{" "}
            and{" "}
            <Link
              to="/ai-resume-builder"
              className="text-zinc-300 hover:text-white underline underline-offset-2"
            >
              AI resume builder
            </Link>
            .
          </p>
        </div>
      </main>
    </div>
  );
}
